import { driverAvatarImageClass } from '@multirx/shared/game/driverAvatarCrop.js';
import {
  pickSeasonStandingLeader,
  seasonStandingDisplayPoints,
  seasonStandingHasScore,
  sortSeasonStandings,
} from '@multirx/shared/game/seasonStandingsSort.js';
import type { ActiveSeasonView } from '@multirx/shared/types/championship.js';
import type { DivisionId } from '@multirx/shared/types/division.js';
import { fetchCarById } from '../cars/api.js';
import { mountCar3dPreview, type Car3dPreviewHandle } from '../cars/car3dPreview.js';
import { resolveCar3dModelUrl } from '../cars/car3dModelMap.js';
import { getOwnDriverId, labelOwnDriver } from '../auth/ownDriverLabel.js';
import { fetchDriverById } from '../drivers/api.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { standingsCompactTableHtml } from '../standings/standingsTableMarkup.js';

export type DivisionCardLeader = {
  driverId: number;
  driverName: string;
  carId: number | null;
  carName: string | null;
  points: number;
  rank: number;
};

export function resolveDivisionCardLeader(view: ActiveSeasonView): DivisionCardLeader | null {
  const top = pickSeasonStandingLeader(view.standings);
  if (!top) return null;
  const gridSlot = view.nextSession?.grid.find((slot) => slot.driverId === top.driverId);
  return {
    driverId: top.driverId,
    driverName: top.driverName,
    carId: gridSlot?.carId ?? null,
    carName: gridSlot?.carName?.trim() || null,
    points: seasonStandingDisplayPoints(top),
    rank: top.rank,
  };
}

export function buildDivisionCardStandingsHtml(view: ActiveSeasonView): string {
  const ranked = sortSeasonStandings(view.standings.filter(seasonStandingHasScore));
  if (ranked.length === 0) return '';
  return standingsCompactTableHtml(
    ranked.map((row, index) => ({
      driverId: row.driverId,
      driverName: row.driverName,
      points: seasonStandingDisplayPoints(row),
      rank: row.rank > 0 ? row.rank : index + 1,
      division: view.season.division,
    })),
    { seasonId: view.season.id, limit: 5, ownDriverId: getOwnDriverId() }
  );
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

/** Bannière (top 5) + avatar leader + nom/voiture — le caller ajoute les actions. */
export function appendDivisionCardProfileShell(
  card: HTMLElement,
  view: ActiveSeasonView,
  opts: {
    division: DivisionId;
    divisionLabel: string;
    circuitLine?: string | null;
    meetingLine?: string | null;
    sessionLine?: string | null;
  }
): void {
  card.classList.add('championship-division-card--profile');

  const banner = document.createElement('div');
  banner.className = 'championship-division-card__banner';

  const bannerHead = document.createElement('div');
  bannerHead.className = 'championship-division-card__banner-head';

  const title = document.createElement('h3');
  title.className = `championship-division-card-title championship-division-card-title--${opts.division}`;
  title.textContent = opts.divisionLabel;
  bannerHead.appendChild(title);

  if (opts.circuitLine) {
    const circuit = document.createElement('p');
    circuit.className = 'championship-division-card-circuit';
    circuit.textContent = opts.circuitLine;
    bannerHead.appendChild(circuit);
  }
  if (opts.meetingLine) {
    const meeting = document.createElement('p');
    meeting.className = 'championship-division-card-meeting';
    meeting.textContent = opts.meetingLine;
    bannerHead.appendChild(meeting);
  }
  if (opts.sessionLine) {
    const session = document.createElement('p');
    session.className = 'championship-division-card-meta';
    session.textContent = opts.sessionLine;
    bannerHead.appendChild(session);
  }
  banner.appendChild(bannerHead);

  const standingsHtml = buildDivisionCardStandingsHtml(view);
  if (standingsHtml) {
    const standingsWrap = document.createElement('div');
    standingsWrap.className = 'championship-division-card__standings';
    standingsWrap.innerHTML = standingsHtml;
    banner.appendChild(standingsWrap);
  } else {
    banner.classList.add('championship-division-card__banner--empty');
  }
  card.appendChild(banner);

  const leader = resolveDivisionCardLeader(view);

  const stage = document.createElement('div');
  stage.className = 'championship-division-card__leader-stage';

  const avatar = document.createElement('div');
  avatar.className = 'championship-division-card__avatar';
  avatar.setAttribute('aria-hidden', leader ? 'false' : 'true');
  if (leader) {
    avatar.textContent = initialsFromName(leader.driverName);
    avatar.dataset.driverId = String(leader.driverId);
  } else {
    avatar.classList.add('championship-division-card__avatar--empty');
    avatar.textContent = '—';
  }
  stage.appendChild(avatar);

  const media = document.createElement('div');
  media.className = 'championship-division-card__media';
  media.setAttribute('aria-hidden', 'true');
  const carPhoto = document.createElement('div');
  carPhoto.className = 'championship-division-card__car-photo';
  const car3d = document.createElement('div');
  car3d.className = 'championship-division-card__car-3d';
  media.append(carPhoto, car3d);
  stage.appendChild(media);
  card.appendChild(stage);

  const body = document.createElement('div');
  body.className = 'championship-division-card__body';

  const nameEl = document.createElement('div');
  nameEl.className = 'championship-division-card__leader-name';
  nameEl.textContent = leader
    ? labelOwnDriver(leader.driverName, leader.driverId)
    : 'Début de la saison';
  body.appendChild(nameEl);

  const carEl = document.createElement('div');
  carEl.className = 'championship-division-card__leader-car';
  carEl.textContent = leader?.carName?.trim() || (leader ? 'Voiture —' : 'Classement en attente');
  body.appendChild(carEl);
  card.appendChild(body);

  if (leader) {
    void enrichDivisionCardLeaderMedia({
      avatarEl: avatar,
      carNameEl: carEl,
      carPhotoEl: carPhoto,
      car3dEl: car3d,
      driverId: leader.driverId,
      division: opts.division,
      knownCarId: leader.carId,
      knownCarName: leader.carName,
    });
  }
}

type EnrichLeaderMediaArgs = {
  avatarEl: HTMLElement;
  carNameEl: HTMLElement;
  carPhotoEl: HTMLElement;
  car3dEl: HTMLElement;
  driverId: number;
  division: DivisionId;
  knownCarId: number | null;
  knownCarName: string | null;
};

async function enrichDivisionCardLeaderMedia(args: EnrichLeaderMediaArgs): Promise<void> {
  const { avatarEl, carNameEl, carPhotoEl, car3dEl, driverId, division, knownCarId, knownCarName } =
    args;
  let preview: Car3dPreviewHandle | null = null;
  try {
    const driver = await fetchDriverById(driverId);
    if (avatarEl.dataset.driverId !== String(driverId)) return;
    const imagePath = driver.imagePath?.trim();
    if (imagePath) {
      const img = document.createElement('img');
      img.src = resolveMediaUrl(imagePath);
      img.alt = `${driver.firstName} ${driver.lastName}`.trim();
      img.className = driverAvatarImageClass(
        'championship-division-card__avatar-img',
        division,
        imagePath
      );
      avatarEl.replaceChildren(img);
    }

    const carId = knownCarId ?? driver.defaultCarId ?? null;
    if (carId == null) return;
    const car = await fetchCarById(carId);
    if (avatarEl.dataset.driverId !== String(driverId)) return;
    if (!knownCarName && car.name?.trim()) {
      carNameEl.textContent = car.name.trim();
    }
    const carImage = car.imagePath?.trim();
    if (carImage) {
      const img = document.createElement('img');
      img.src = resolveMediaUrl(carImage);
      img.alt = car.name?.trim() || 'Voiture';
      img.className = 'championship-division-card__car-photo-img';
      carPhotoEl.replaceChildren(img);
      carPhotoEl.classList.add('is-ready');
    }
    const modelUrl = resolveCar3dModelUrl(car);
    preview = mountCar3dPreview(car3dEl, modelUrl, undefined, { snapshot: true });
    car3dEl.classList.add('is-ready');
  } catch {
    preview?.dispose();
  }
}
